import React from 'react';
import { useParams } from 'react-router-dom';
import { UserPlusIcon } from 'lucide-react';
import { usePortalQuery, usePortalMutation } from '../../hooks/usePortalQuery';
import { inviteMember, listMembers, removeMember, updateMemberRole } from '../../lib/portalApi';
import { can, ROLE_DESCRIPTIONS, ROLE_LABELS } from '../../lib/portalPermissions';
import { usePortalAuth } from '../../context/PortalAuth';
import { orgName } from '../../context/PortalOrg';
import { Button } from '../../components/ui/Button';
import { Field, inputClass } from '../../components/ui/Field';
import { PageHeader } from '../../components/portal/PageHeader';
import { Avatar } from '../../components/portal/Avatar';
import { Dialog } from '../../components/portal/Dialog';
import { InlineError, QueryState } from '../../components/portal/QueryState';
import { displayName, formatDate } from '../../utils/portalFormat';
import type { OrgRole } from '../../types/portal';
const ROLES: OrgRole[] = ['owner', 'admin', 'member', 'viewer'];
export function PortalTeam() {
  const {
    orgId = ''
  } = useParams<{
    orgId: string;
  }>();
  const {
    roleIn,
    user
  } = usePortalAuth();
  const role = roleIn(orgId);
  const {
    data,
    loading,
    error
  } = usePortalQuery(() => listMembers(orgId), [orgId]);
  const changeRole = usePortalMutation(updateMemberRole);
  const remove = usePortalMutation(removeMember);
  const [inviting, setInviting] = React.useState(false);
  return <div data-id="element-3228" data-mp="3cbj1l124zsv2">
      <PageHeader eyebrow={orgName(orgId)} title="Team" description="People with access to this organisation. Roles apply here only, so the same person can hold a different role elsewhere." actions={can(role, 'members.invite') && <Button size="sm" onClick={() => setInviting(true)} data-id="element-3230" data-mp="3q2nvdmitpqxh">
              <UserPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-3231" data-mp="3v323f4kfd0vi" />
              Invite someone
            </Button>} data-id="element-3229" data-mp="3cbst4t2ty82v" />

      <div className="mt-6 space-y-2" data-id="element-3232" data-mp="3xagsf5uw6dg">
        <InlineError error={changeRole.error ?? remove.error} data-id="element-3233" data-mp="3u4o6py0qmcz" />
      </div>

      <div className="mt-4" data-id="element-3234" data-mp="3ae0uqr1qn64w">
        <QueryState loading={loading} error={error} data-id="element-3235" data-mp="35eockjltan8n">
          <ul className="divide-y divide-line rounded-md border border-line" data-id="element-3236" data-mp="35g7ro6vqes8u">
            {data?.map(m => {
            const self = m.userId === user?.id;
            const pending = !m.user.lastLoginAt;
            return <li key={m.id} className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center" data-id="element-3237" data-mp="3fazrzigp7t7b">
                  <div className="flex min-w-0 flex-1 items-center gap-3" data-id="element-3238" data-mp="3o561ed3iwyoq">
                    <Avatar user={m.user} data-id="element-3239" data-mp="3o4w5k8vsrxb4" />
                    <div className="min-w-0" data-id="element-3240" data-mp="6xkbzrztgm57">
                      <p className="truncate text-[15px] text-ink" data-id="element-3241" data-mp="6w99dvi3xkfo">
                        {displayName(m.user)}
                        {self && <span className="text-muted" data-id="element-3242" data-mp="1w3fs9sa1nuy"> (you)</span>}
                      </p>
                      <p className="truncate text-[13px] text-muted" data-id="element-3243" data-mp="28q8kafrbhzru">
                        {m.user.firstName ? m.user.email : ''}
                        {pending ? `${m.user.firstName ? ', ' : ''}invited ${formatDate(m.joinedAt)}, not yet signed in` : m.user.jobTitle ? `, ${m.user.jobTitle}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-3244" data-mp="9gxffgg2ejc2">
                    {can(role, 'members.changeRole') && !self ? <>
                        <label htmlFor={`role-${m.userId}`} className="sr-only" data-id="element-3245" data-mp="g92kp2eqp0r">
                          Role for {displayName(m.user)}
                        </label>
                        <select id={`role-${m.userId}`} value={m.role} onChange={e => void changeRole.run(orgId, m.userId, e.target.value as OrgRole)} className="h-9 rounded-sm border border-line bg-charcoal px-2 text-[13px] text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-3246" data-mp="4g15om9182yq">
                          {ROLES.map(r => <option key={r} value={r} data-id="element-3247" data-mp="3ly241zamyb5n">
                              {ROLE_LABELS[r]}
                            </option>)}
                        </select>
                      </> : <span className="rounded-full border border-line px-2.5 py-0.5 text-[12px] text-body" data-id="element-3248" data-mp="37zlp5l2mdecf">{ROLE_LABELS[m.role]}</span>}
                    {can(role, 'members.remove') && !self && (m.role !== 'owner' || role === 'owner') && <button type="button" onClick={() => window.confirm(`Remove ${displayName(m.user)} from ${orgName(orgId)}? They lose access straight away.`) && void remove.run(orgId, m.userId)} className="h-9 rounded-sm px-3 text-[13px] text-muted transition-colors duration-150 hover:bg-panel hover:text-[#FFA192]" data-id="element-3249" data-mp="2y7fw080msqvn">
                        Remove
                      </button>}
                  </div>
                </li>;
          })}
          </ul>
        </QueryState>
      </div>

      <section aria-labelledby="roles-explained" className="mt-10" data-id="element-3250" data-mp="14qtfzvnof6t2">
        <h2 id="roles-explained" className="text-[15px] font-medium text-ink" data-id="element-3251" data-mp="14ofm3q4qg0n3">
          What each role can do
        </h2>
        <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2" data-id="element-3252" data-mp="c1ussk4p5egw">
          {ROLES.map(r => <div key={r} data-id="element-3253" data-mp="h0z71p6ha2e3">
              <dt className="text-[14px] text-ink" data-id="element-3254" data-mp="h0683adrgh6i">{ROLE_LABELS[r]}</dt>
              <dd className="mt-0.5 text-[14px] text-muted" data-id="element-3255" data-mp="l2klvg2koqqx">{ROLE_DESCRIPTIONS[r]}</dd>
            </div>)}
        </dl>
      </section>

      <InviteDialog orgId={orgId} canGrantOwner={role === 'owner'} open={inviting} onClose={() => setInviting(false)} data-id="element-3256" data-mp="pu2oe8k8uze0" />
    </div>;
}
function InviteDialog({
  orgId,
  canGrantOwner,
  open,
  onClose
}: {
  orgId: string;
  canGrantOwner: boolean;
  open: boolean;
  onClose: () => void;
}) {
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState<OrgRole>('member');
  const invite = usePortalMutation(inviteMember);
  React.useEffect(() => {
    if (open) {
      setEmail('');
      setRole('member');
      invite.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const member = await invite.run(orgId, {
      email,
      role
    });
    if (member) onClose();
  };
  const options = ROLES.filter(r => r !== 'owner' || canGrantOwner);
  return <Dialog open={open} onClose={onClose} title="Invite someone" description="They receive a sign in link by email. No password is needed." data-id="element-3257" data-mp="qpygcn4ntev1">
      <form onSubmit={submit} className="space-y-5" data-id="element-3258" data-mp="mmtfsq453n91">
        <Field id="invite-email" label="Work email" required error={invite.error?.details.email} data-id="element-3259" data-mp="224bwf5t68q0i">
          <input id="invite-email" type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} autoComplete="off" data-id="element-3260" data-mp="1y3t8hmueg43l" />
        </Field>
        <fieldset data-id="element-3261" data-mp="25p5dcfmtuo4r">
          <legend className="text-[14px] font-medium text-ink" data-id="element-3262" data-mp="2alm47hlevglj">Role</legend>
          <div className="mt-3 space-y-2" data-id="element-3263" data-mp="2ajj2w1et611i">
            {options.map(r => <label key={r} className={`flex cursor-pointer gap-3 rounded-sm border px-4 py-3 transition-colors duration-150 ${role === r ? 'border-gold bg-charcoal' : 'border-line hover:border-gold/60'}`} data-id="element-3264" data-mp="1vqfvgmuxl3oi">
                <input type="radio" name="invite-role" value={r} checked={role === r} onChange={() => setRole(r)} className="mt-1 h-4 w-4 accent-[#FF5C1A]" data-id="element-3265" data-mp="2ofdcynrbiwg7" />
                <span data-id="element-3266" data-mp="2emfqy7n4apcu">
                  <span className="block text-[14px] text-ink" data-id="element-3267" data-mp="2emxtfm0dhzt7">{ROLE_LABELS[r]}</span>
                  <span className="block text-[13px] text-muted" data-id="element-3268" data-mp="2jl93nxukycby">{ROLE_DESCRIPTIONS[r]}</span>
                </span>
              </label>)}
          </div>
        </fieldset>
        {invite.error && !invite.error.details.email && <InlineError error={invite.error} data-id="element-3269" data-mp="3c4xxvc7183s4" />}
        <div className="flex gap-3" data-id="element-3270" data-mp="1c0vdpza9yywf">
          <Button type="submit" disabled={invite.pending} data-id="element-3271" data-mp="1c1wp64v7dneo">
            {invite.pending ? 'Sending invitation' : 'Send invitation'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} data-id="element-3272" data-mp="137s3dzuxf260">
            Cancel
          </Button>
        </div>
      </form>
    </Dialog>;
}